import { APP_INITIALIZER, ApplicationConfig, inject, provideBrowserGlobalErrorListeners } from '@angular/core';
import { QueryClient, provideTanStackQuery } from '@tanstack/angular-query-experimental';
import { Routes, withViewTransitions } from '@angular/router';
import { authHttpInterceptorFn, provideAuth0 } from '@auth0/auth0-angular';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { requireNoProfileGuard, requireProfileGuard } from './app/core/guards/profile.guard';
import { provideTransloco } from '@jsverse/transloco';

import { API_BASE_URL } from './api/api-clients';
import { ErrorPage } from './app/core/errors/error.page';
import { LanguageService } from './app/core/language.service';
import { Root } from './app/root';
import { Shell } from './app/layout/shell';
import { TranslocoHttpLoader } from './app/core/transloco-loader';
import { bootstrapApplication } from '@angular/platform-browser';
import { environment } from './environments/environment';
import { provideRouter } from '@angular/router';
import { withDevtools } from '@tanstack/angular-query-experimental/devtools';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./app/modules/home/home').then((m) => m.Home),
  },
  {
    path: 'register',
    canActivate: [requireNoProfileGuard],
    loadChildren: () => import('./app/modules/onboarding/onboarding.routes'),
  },
  {
    path: 'app',
    component: Shell,
    canActivate: [requireProfileGuard],
    children: [
      { path: 'profile', loadChildren: () => import('./app/modules/profile/profile.routes') },
      { path: '', redirectTo: 'profile', pathMatch: 'full' },
    ],
  },
  { path: '**', component: ErrorPage },
];

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withInterceptors([authHttpInterceptorFn])),
    provideTanStackQuery(new QueryClient(), ...(environment.production ? [] : [withDevtools()])),
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes, withViewTransitions()),
    provideTransloco({
      config: {
        availableLangs: ['es', 'en', 'fr', 'pt'],
        defaultLang: 'es',
        reRenderOnLangChange: true,
        prodMode: environment.production,
      },
      loader: TranslocoHttpLoader,
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        const languageService = inject(LanguageService);
        return () => languageService.init();
      },
      multi: true,
    },
    provideAuth0({
      domain: environment.auth.domain,
      clientId: environment.auth.clientId,
      cacheLocation: 'localstorage',
      useRefreshTokens: true,
      authorizationParams: {
        audience: environment.auth.audience,
        redirect_uri: `${window.location.origin}/app`,
      },
      httpInterceptor: {
        allowedList: [`${environment.apiBaseUrl}/api/*`],
      },
    }),
    { provide: API_BASE_URL, useValue: environment.apiBaseUrl },
  ],
};

bootstrapApplication(Root, appConfig).catch((err) => console.error(err));
